import { Upload, Video, X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

interface VideoUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
  onSelect?: () => void;
  onSuccess?: (file: File) => void;
  onError?: (message: string) => void;
}

const MAX_SIZE = 100 * 1024 * 1024; // 100 MB

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function VideoUpload({
  value,
  onChange,
  onSelect,
  onSuccess,
  onError,
}: VideoUploadProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    const isVideo =
      file.type.startsWith("video/") || /\.(mp4|mov|m4v|avi|webm)$/i.test(file.name);
    if (!isVideo) {
      const formatError = "Please upload a video file (MP4, MOV, etc.)";
      onError?.(formatError);
      toast.error(formatError);
      return;
    }
    if (file.size > MAX_SIZE) {
      const sizeError = "Video is too large (max 100 MB)";
      onError?.(sizeError);
      toast.error(sizeError);
      return;
    }
    onChange(file);
    onSuccess?.(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    onSelect?.();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  if (value) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-card">
            <Video className="h-6 w-6 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-sm text-zinc-800">{value.name}</p>
            <p className="text-xs text-zinc-400">{formatBytes(value.size)}</p>
          </div>
          <button
            onClick={() => onChange(null)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition-transform hover:scale-110 active:scale-95"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-3 text-xs text-zinc-500">
          ✅ 3 frames will be extracted automatically at the beginning, middle, and end of
          your video.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 transition-all sm:p-8 ${
          isDragging
            ? "border-primary bg-background"
            : "border-zinc-300 bg-white hover:border-primary hover:bg-background"
        }`}
      >
        <input
          type="file"
          accept="video/mp4,video/quicktime,video/*,.mp4,.mov,.m4v"
          className="hidden"
          onChange={handleInput}
          onClick={onSelect}
        />
        <div
          className={`mb-3 flex h-14 w-14 items-center justify-center rounded-2xl ${
            isDragging ? "bg-card" : "bg-zinc-100"
          }`}
        >
          {isDragging ? (
            <Upload className="h-6 w-6 text-primary" />
          ) : (
            <Video className="h-6 w-6 text-zinc-400" />
          )}
        </div>
        <p className="font-medium text-sm text-zinc-600">
          Drop video here or <span className="text-primary">browse</span>
        </p>
        <p className="mt-1 text-xs text-zinc-400">MP4, MOV · max 100 MB</p>
      </label>

      <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-3 text-xs text-zinc-500 leading-relaxed">
        <p className="mb-1 font-medium text-zinc-600">
          📹 Record a short video (10–15 sec):
        </p>
        <p>— Look at the camera, slowly turn your head left and right</p>
        <p>
          — Read aloud: <em>"Hi, my name is [name]. Nice to meet you!"</em>
        </p>
      </div>
    </div>
  );
}
